import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai/index';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { Toggle } from '@flipster/react/design-system/toggle';
import { useResponsive } from '@flipster/react/misc/responsive/legacy';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { Drawer } from '@flipster/ui';
import segment from '@flipster/utils/segment';
import { TradingLeaderboardEvent } from '@flipster/utils/segment/events/trading-leaderboard';

import { intervalLeaderboardMyRankingBunja } from '../../bunja';

interface Props {
  initialSettings: { optOut: boolean };
  onFinish: () => void;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const SettingsOverlay = ({ initialSettings, onFinish, isOpen, onClose, onExit }: Props) => {
  const t = useTranslations();
  // Drawer 컴포넌트가 useResponsive 기반이라서 부득이하게 media query 대신 적용하였음
  const { isMobile } = useResponsive();
  const [isOptOut, setIsOptOut] = useState(initialSettings.optOut);

  const { putTradingLeaderboardsOptOutAtom } = useBunja(intervalLeaderboardMyRankingBunja);
  const optOutMutation = useAtomValue(putTradingLeaderboardsOptOutAtom);

  const handleToggleOptOut = () => {
    setIsOptOut((v) => !v);
  };

  const handleClickDoneSettings = async () => {
    if (isOptOut !== initialSettings.optOut) {
      segment.track(TradingLeaderboardEvent.LeaderboardSettingConfirmClick, { view_my_rank: !isOptOut }); // opt-in: true, opt-out: false
      await optOutMutation.mutateAsync({ optOut: isOptOut });
    }
    onFinish();
  };

  if (!isMobile) {
    return (
      <Modal isOpen={isOpen} onExit={onExit}>
        <Modal.Close onClose={onClose} />
        <Modal.Header>
          <Modal.Title label={t('Autogen_common.r251126.s_Settings')} />
        </Modal.Header>
        <Modal.Content>
          <div className={css({ bg: 'background.default.level-3', rounded: 8, p: '12px 16px' })}>
            <div className={hstack({ justify: 'space-between', mb: 8 })}>
              <span className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
                <FormattedMessage id='Autogen_common.r251126.s_View_my_Rank' />
              </span>
              <Toggle selected={!isOptOut} onChange={handleToggleOptOut} />
            </div>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
              <FormattedMessage id='Autogen_common.r251126.s_Your_ranking_is_shown_on_the_leaderboard_by_default_If_disabled_it_won_t_appear_in_the_rankings' />
            </p>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button label={t('Autogen_common.r251126.s_Done')} onClick={handleClickDoneSettings} size='large' />
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Drawer
      isShow={isOpen}
      onExit={onExit}
      position='bottom'
      className={css({ bg: 'linear-gradient(179deg, #1C1C1C 46.21%, #111 87.14%)' })}
    >
      <div className={css({ pb: 20, textStyle: 'Title/S', color: 'inverse.white' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Settings' />
      </div>
      <div className={css({ mb: 32, bg: 'background.default.level-3', rounded: 8, p: '12px 16px' })}>
        <div className={hstack({ justify: 'space-between', mb: 8 })}>
          <span className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
            <FormattedMessage id='Autogen_common.r251126.s_View_my_Rank' />
          </span>
          <Toggle selected={!isOptOut} onChange={handleToggleOptOut} />
        </div>
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Your_ranking_is_shown_on_the_leaderboard_by_default_If_disabled_it_won_t_appear_in_the_rankings' />
        </p>
      </div>
      <Button
        label={t('Autogen_common.r251126.s_Done')}
        onClick={handleClickDoneSettings}
        size='large'
        css={css.raw({ width: '100%' })}
      />
    </Drawer>
  );
};
